import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CustomHelmet from 'components/CustomHelmet';
import styled from 'styled-components';
import {
  useSelector, useDispatch,
} from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { APP } from 'appenv';
import useCachedCMSDataTreatment from 'hooks/useCachedCMSDataTreatment';
import {
  fetchMetaBooths,
  boothIdsGroupedByTierIds,
  selectFilteredBoothIds,
  selectTotalFilteredBoothsCount,
} from 'models/cms/booths';
import {
  getFilterOptions,
  selectBoothTaxonomiesFetchingState,
  selectSelectedCategoryIds,
} from 'models/cms/boothTaxonomies';
import { useTranslation } from 'react-i18next';
import { selectLocale } from 'models/localization';
import { selectLocalizedEventName } from 'models/virtual_event';
import { SplitioContext } from 'components/SplitioContext';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import Routes from 'Routes';

import useFilter from './filterTemplates/useFilter';
import HallListComponent from './HallListComponent';
import BoothFilterSwitch from './BoothFilter';
import 'objectFitPolyfill';
import LiveStreamStatusSubscriber from '../../components/pictureInPicture/LiveStreamStatusSubscriber';

const RootContainer = styled.div`
  min-width: 100%;
  height: 100%;
  overflow: hidden;

  @media (min-width: 960px) {
    background: url(${({ backgroundImg }) => backgroundImg});
    background-color: ${({ backgroundImg }) => (!backgroundImg && '#F0F1F2')};
    background-position: center;
    background-repeat: no-repeat;
    background-size: 1440px auto;
  }

  @media (max-width: 960px) {
    background-color: #F0F1F2;
  }

  @media (min-width: 1440px), (min-height: 960px) {
    background-size: cover;
  }
`;

const BoothFilterListContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 960px) {
    flex-direction: row;
  }
`;

const BoothListContainer = styled.div`
  width: ${({ hasFilter }) => (hasFilter ? 'calc(100% - 300px)' : '100%')};
  height: 100%;
  overflow: ${APP === 'scmp711' ? 'auto' : 'hidden'};
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: row;
  align-content: flex-start;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  margin: auto;

  @media only screen and (max-width: 959px) {
    width: 100%;
  }
`;

const BackgroundVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;

  @media only screen and (max-width: 959px) {
    display: none;
  }
`;

const BoothListWrapper = styled.div`
  height: 100%;
  flex-basis: 100%;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  border-radius: 0 !important;
  padding: 0;
  width: inherit;
  flex-direction: column;
  justify-content: flex-start;
`;

const HallPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    selectedFilter,
    setSelectedFilter,
    subLevelOnClick,
    onClick,
    resetSelectedFilter,
  } = useFilter();
  const {
    pageNames,
    hall,
    features,
  } = useSelector((state) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);
  const locale = useSelector(selectLocale);
  const cachedCMSDataReady = useSelector((state) => state.cms.events.cachedCMSDataReady);
  const multipleHall = useMemo(() => features.includes('multipleHall'), [features]);
  const selectedCategoryIds = useSelector(selectSelectedCategoryIds);
  const filterOptions = useSelector(getFilterOptions());
  const exhibitorIds = useSelector(selectFilteredBoothIds);
  const numOfExhibitors = useSelector(selectTotalFilteredBoothsCount);
  const [showCollapse, setShowCollapse] = useState([]);
  const isFetchingBoothTaxonomies = useSelector(selectBoothTaxonomiesFetchingState);
  const hallBackgroundImage = typeof hall?.backgroundImgSrc === 'string'
    ? hall?.backgroundImgSrc
    : 'https://spa-assets.eventxtra.com/6enD87uZ9ikVnugXUNmAH5G8AEySveqadd5XyTAdyP1q/Hall%201--1615171662239.jpeg';

  useEffect(() => {
    window.objectFitPolyfill();
    if (!multipleHall) window.trackingEventToFirebase(null, 'Main_Hall', 'Enter', '');
  }, [multipleHall]);

  useEffect(() => {
    const { searchParams } = new URL(window.location.href);
    const boothFilter = JSON.parse(searchParams.get('boothFilter'));
    if (multipleHall && searchParams.get('hallName')) {
      if (filterOptions.length > 0) {
        setShowCollapse(
          filterOptions.map(({ subcategories }) => (
            subcategories.map(({ id }) => id))).map((hallFilter) => hallFilter.some((filter) => (boothFilter.includes(filter)))),
        );
      }
      setSelectedFilter(boothFilter || []);
      window.trackingEventToFirebase(null, 'Multiple_Hall', 'Enter', searchParams.get('hallName'));
    }
  }, [setSelectedFilter, multipleHall, filterOptions]);

  const { isReady: isSplitioReady } = useContext(SplitioContext);
  const cachedCMSData = useCachedCMSDataTreatment();

  useEffect(() => {
    if (!isSplitioReady || (cachedCMSData && !cachedCMSDataReady)) return () => {};
    const promise = dispatch(fetchMetaBooths({ boothCount: 20 }));
    return () => promise.abort();
  }, [selectedCategoryIds, dispatch, isSplitioReady, cachedCMSData, cachedCMSDataReady]);

  const exhibitorIdsGroupedByTierIds = useSelector(boothIdsGroupedByTierIds);
  const hasFilter = useMemo(
    () => (filterOptions?.length > 0 || Object.keys(filterOptions).length > 0),
    [filterOptions],
  );

  const pageTitle = pageNames.hall?.[locale] || t('page.hall', 'Exhibition Hall');

  return (
    <RootContainer backgroundImg={hallBackgroundImage}>
      <LiveStreamStatusSubscriber />
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`${pageTitle} | ${eventName}`}</title>
        <meta name="description" content={`${pageTitle} page of ${eventName}.`} />
      </CustomHelmet>
      {
        !!hall?.backgroundVideoSrc && (
          <BackgroundVideo autoPlay loop muted playsInline data-object-fit="cover">
            <source src={hall?.backgroundVideoSrc} type="video/mp4" />
          </BackgroundVideo>
        )
      }
      <BoothFilterListContainer>
        {hasFilter && (
          <BoothFilterSwitch
            filterOptions={filterOptions}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            resetSelectedFilter={resetSelectedFilter}
            numOfExhibitors={numOfExhibitors}
            subLevelOnClick={subLevelOnClick}
            onClick={onClick}
            isFetchingFilter={isFetchingBoothTaxonomies}
            showCollapse={showCollapse}
            setShowCollapse={setShowCollapse}
          />
        )}
        <BoothListContainer>
          <BoothListWrapper>
            <HallListComponent
              exhibitorIds={exhibitorIds}
              exhibitorIdsGroupedByTierIds={exhibitorIdsGroupedByTierIds}
              isFiltered={!isEmpty(selectedCategoryIds)}
              hasFilter={hasFilter}
            />
          </BoothListWrapper>
          <FloatingFooter hashState={Routes.hall} />
        </BoothListContainer>
      </BoothFilterListContainer>
    </RootContainer>
  );
};

export default HallPage;
